// src/components/admin/PersonalisationTargeting.jsx
// "Customer Targeting" section for Portal Personalisation Campaigns. Same audience options and
// criteria as the Email / Push / WhatsApp editors (Save Targeting Criteria + Preview Target Customers).
import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { getCustomerTargetingPreview, getSalonServices, getSalonStaff } from '../../api/salonApi';
import { useSalonLocations } from '../../hooks/useSalonLocations';
import '../../styles/customer-preview-modal.css';

const APPOINTMENT_STATUS_OPTIONS = ['approved', 'pending', 'rejected', 'cancelled'];
const BIRTH_MONTH_OPTIONS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const MEMBER_TIER_OPTIONS = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'];

export const emptyTargetingCriteria = () => ({
  birthMonths: [],
  postalCodes: [],
  lastTransactionMonths: 0,
  lastTransactionUnit: 'months',
  transactionCount: 0,
  transactionType: 'not_selected',
  transactionAmountType: 'not_selected',
  transactionAmount: 0,
  transactionLocationId: 'not_selected',
  memberTiers: [],
  pointsType: 'not_selected',
  pointsAmount: 0,
  specificCustomers: [],
  notesSearchText: '',
  notesMatchType: 'contains_all',
  appointmentTimeValue: 0,
  appointmentTimeUnit: 'days',
  appointmentService: 'not_selected',
  appointmentStaff: 'not_selected',
  appointmentStatus: []
});

export const initialTargeting = () => ({
  targetingType: 'not_selected',
  targetingCriteria: emptyTargetingCriteria()
});

/** Returns { fieldName: message }; empty when targeting is valid. */
export const validateTargeting = (targeting) => {
  const errors = {};
  if (targeting.targetingType === 'not_selected') {
    errors.targetingType = 'Target audience is required';
  }
  if (targeting.targetingType === 'specific_customer' &&
      targeting.targetingCriteria.specificCustomers.length === 0) {
    errors.specificCustomers = 'Please select at least one customer for specific customer targeting';
  }
  return errors;
};

// Compact recipients preview (same table styling as the other channels' preview modal)
function TargetingPreviewModal({ isOpen, onClose, previewData }) {
  const [filterText, setFilterText] = useState('');
  if (!isOpen || !previewData) return null;

  const { eligibleCustomers = [], eligibleCount = 0, totalCount = 0 } = previewData;
  const needle = filterText.toLowerCase();
  const rows = eligibleCustomers
    .filter(c => !needle || c.displayName?.toLowerCase().includes(needle) || c.email?.toLowerCase().includes(needle))
    .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  const coverage = totalCount > 0 ? ((eligibleCount / totalCount) * 100).toFixed(1) : '0.0';

  return (
    <div className="modal-overlay">
      <div className="customer-preview-modal">
        <div className="modal-header">
          <h3>Targeted Customers Preview</h3>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-summary">
          <div className="summary-item">
            <span className="summary-label">Targeted Customers</span>
            <span className="summary-value">{eligibleCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Customers</span>
            <span className="summary-value">{totalCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Coverage</span>
            <span className="summary-value coverage">{coverage}%</span>
          </div>
        </div>
        <div className="modal-body">
          {eligibleCount > 0 ? (
            <>
              <div className="table-filter">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
                {filterText && (
                  <button type="button" className="filter-clear-btn" onClick={() => setFilterText('')}>Clear</button>
                )}
              </div>
              <div className="customer-table-container">
                <table className="customer-table">
                  <thead>
                    <tr><th>Customer Name</th><th>Email Address</th></tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 200).map((customer, index) => (
                      <tr key={customer.id || index}>
                        <td>{customer.displayName || 'N/A'}</td>
                        <td>{customer.email || 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 200 && (
                <div className="form-helper-text">Showing the first 200 of {rows.length} matching customers.</div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">👥</div>
              <p className="empty-state-text">No customers match the selected criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PersonalisationTargeting({ salonId, value, onChange, errors = {} }) {
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;
  const { targetingType, targetingCriteria } = value;

  const [criteriaSaved, setCriteriaSaved] = useState(false);
  const [savedNotice, setSavedNotice] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewError, setPreviewError] = useState('');

  const [salonServices, setSalonServices] = useState([]);
  const [salonStaff, setSalonStaff] = useState([]);
  const [loadingServicesStaff, setLoadingServicesStaff] = useState(false);

  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [searchingCustomers, setSearchingCustomers] = useState(false);
  const allCustomersRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Services and staff feed the appointment-based targeting dropdowns
  useEffect(() => {
    if (!salonId) return undefined;
    let cancelled = false;
    setLoadingServicesStaff(true);
    Promise.all([getSalonServices(salonId), getSalonStaff(salonId)])
      .then(([services, staff]) => {
        if (cancelled) return;
        setSalonServices(services.filter(s => s.isActive !== false));
        setSalonStaff(staff.filter(s => s.isActive !== false));
      })
      .catch(err => console.error('Error loading services and staff:', err))
      .finally(() => { if (!cancelled) setLoadingServicesStaff(false); });
    return () => { cancelled = true; };
  }, [salonId]);

  // Close the customer dropdown when clicking elsewhere
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.customer-search-container')) setShowCustomerDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => () => clearTimeout(searchTimeoutRef.current), []);

  const handleTargetingTypeChange = (nextType) => {
    setCriteriaSaved(false);
    setSavedNotice(false);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    onChange({ targetingType: nextType, targetingCriteria: emptyTargetingCriteria() });
  };

  const handleCriteriaChange = (field, fieldValue) => {
    setCriteriaSaved(false);
    setSavedNotice(false);
    onChange({ targetingType, targetingCriteria: { ...targetingCriteria, [field]: fieldValue } });
  };

  const selectedOptions = (e) => Array.from(e.target.selectedOptions, option => option.value);

  const handleSaveCriteria = () => {
    setCriteriaSaved(true);
    setSavedNotice(true);
    setTimeout(() => setSavedNotice(false), 3000);
  };

  const handlePreview = async () => {
    try {
      setPreviewLoading(true);
      setPreviewError('');
      const preview = await getCustomerTargetingPreview(salonId, { targetingType, targetingCriteria });
      setPreviewData(preview);
      setShowPreview(true);
    } catch (err) {
      console.error('Error getting preview:', err);
      setPreviewError('Failed to get targeting preview. Please try again.');
      setTimeout(() => setPreviewError(''), 5000);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadSocialCSV = async () => {
    try {
      const preview = await getCustomerTargetingPreview(salonId, { targetingType, targetingCriteria });
      const customers = preview.eligibleCustomers || [];
      const rows = [['first_name', 'last_name', 'email', 'phone']];
      customers.forEach(c => {
        const nameParts = (c.displayName || '').trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        const email = (c.email || '').toLowerCase();
        const digitsOnly = (c.phone || '').replace(/\D/g, '');
        const phone = digitsOnly.length === 8 ? `65${digitsOnly}` : digitsOnly;
        rows.push([firstName, lastName, email, phone]);
      });
      const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'social_platform_audience.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading social CSV:', err);
      setPreviewError('Failed to generate CSV. Please try again.');
      setTimeout(() => setPreviewError(''), 5000);
    }
  };

  // Customers are fetched once and searched locally so the search covers the whole customer base
  const searchCustomers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomerSearchResults([]);
      return;
    }
    setSearchingCustomers(true);
    try {
      if (!allCustomersRef.current) {
        const snapshot = await getDocs(query(collection(db, 'customers'), where('salonId', '==', salonId)));
        allCustomersRef.current = snapshot.docs.map(d => ({
          id: d.id,
          email: d.data().email || null,
          displayName: d.data().displayName || null,
          phoneNumber: d.data().phoneNumber || null,
          uid: d.data().uid || null
        }));
      }
      const needle = searchTerm.toLowerCase();
      const alreadySelected = new Set(targetingCriteria.specificCustomers.map(c => c.id));
      setCustomerSearchResults(
        allCustomersRef.current
          .filter(c => !alreadySelected.has(c.id) &&
            (c.email?.toLowerCase().includes(needle) || c.displayName?.toLowerCase().includes(needle)))
          .slice(0, 10)
      );
    } catch (err) {
      console.error('Error searching customers:', err);
      setCustomerSearchResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  };

  const handleCustomerSearchChange = (e) => {
    const next = e.target.value;
    setCustomerSearchQuery(next);
    setShowCustomerDropdown(true);
    clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchCustomers(next), 300);
  };

  const handleCustomerSelect = (customer) => {
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setShowCustomerDropdown(false);
    handleCriteriaChange('specificCustomers', [...targetingCriteria.specificCustomers, customer]);
  };

  const handleCustomerRemove = (customerId) => {
    handleCriteriaChange(
      'specificCustomers',
      targetingCriteria.specificCustomers.filter(c => c.id !== customerId)
    );
  };

  return (
    <div className="form-section">
      <h4>Customer Targeting</h4>

      {previewError && <div className="error-message">{previewError}</div>}
      {savedNotice && <div className="success-message">Targeting criteria saved successfully!</div>}

      <TargetingPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        previewData={previewData}
      />

      <div className="form-group">
        <label htmlFor="personalisationTargetingType">
          Target Audience<span className="required-asterisk">*</span>
        </label>
        <select
          id="personalisationTargetingType"
          value={targetingType}
          onChange={(e) => handleTargetingTypeChange(e.target.value)}
          className={errors.targetingType ? 'input-error' : ''}
        >
          <option value="not_selected">Not Selected</option>
          <option value="all_customers">All Customers</option>
          <option value="customer_profile">Customer Profile Based</option>
          <option value="customer_transaction">Customer Transaction Based</option>
          <option value="customer_points">Customer Points Based</option>
          <option value="customer_appointment">Customer Appointment Based</option>
          <option value="specific_customer">Specific Customer</option>
          <option value="customer_notes">Customer Notes Based</option>
        </select>
        {errors.targetingType && <span className="field-error-message">{errors.targetingType}</span>}
      </div>

      {targetingType === 'customer_profile' && (
        <div className="targeting-criteria">
          <div className="form-group">
            <label>Birth Months</label>
            <select
              multiple
              value={targetingCriteria.birthMonths}
              onChange={(e) => handleCriteriaChange('birthMonths', selectedOptions(e))}
              size="6"
            >
              {BIRTH_MONTH_OPTIONS.map(month => <option key={month} value={month}>{month}</option>)}
            </select>
            <div className="form-helper-text">Hold Ctrl/Cmd to select multiple months</div>
          </div>

          <div className="form-group">
            <label>Postal Codes (comma-separated)</label>
            <input
              type="text"
              defaultValue={targetingCriteria.postalCodes.join(', ')}
              onChange={(e) => handleCriteriaChange(
                'postalCodes',
                e.target.value.split(',').map(code => code.trim()).filter(code => code)
              )}
              placeholder="e.g., 560, 123456, 567890"
            />
            <div className="form-helper-text">
              Leave empty for all postal codes. Use partial codes for broader targeting.
            </div>
          </div>
        </div>
      )}

      {targetingType === 'customer_transaction' && (
        <div className="targeting-criteria">
          {isMultiLocation && (
            <div className="form-group">
              <label>Location Name</label>
              <select
                value={targetingCriteria.transactionLocationId || 'not_selected'}
                onChange={(e) => handleCriteriaChange('transactionLocationId', e.target.value)}
              >
                <option value="not_selected">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.locationId} value={loc.locationId}>
                    {loc.locationName}{loc.isHeadOffice ? ' (Head Office)' : ''}
                  </option>
                ))}
              </select>
              <div className="form-helper-text">
                Filter by transactions at a specific location, or leave as "All Locations".
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Transaction Count Criteria</label>
            <select
              value={targetingCriteria.transactionType}
              onChange={(e) => handleCriteriaChange('transactionType', e.target.value)}
            >
              <option value="not_selected">Not Selected</option>
              <option value="less_than">Less than X transactions</option>
              <option value="equal_to">Exactly X transactions</option>
              <option value="greater_than">More than X transactions</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Number of Transactions</label>
              <input
                type="number"
                value={targetingCriteria.transactionCount}
                onChange={(e) => handleCriteriaChange('transactionCount', Number(e.target.value))}
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Last Transaction within</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="number"
                  value={targetingCriteria.lastTransactionMonths}
                  onChange={(e) => handleCriteriaChange('lastTransactionMonths', Number(e.target.value))}
                  min="0"
                  style={{ width: '80px' }}
                />
                <select
                  value={targetingCriteria.lastTransactionUnit || 'months'}
                  onChange={(e) => handleCriteriaChange('lastTransactionUnit', e.target.value)}
                >
                  <option value="hours">Hours</option>
                  <option value="days">Days</option>
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Spend Amount Criteria</label>
            <select
              value={targetingCriteria.transactionAmountType}
              onChange={(e) => handleCriteriaChange('transactionAmountType', e.target.value)}
            >
              <option value="not_selected">Not Selected</option>
              <option value="greater_than">Greater Than the Following Amount</option>
              <option value="less_than">Less Than the Following Amount</option>
            </select>
          </div>

          <div className="form-group">
            <label>Amount ($)</label>
            <input
              type="number"
              value={targetingCriteria.transactionAmount}
              onChange={(e) => handleCriteriaChange('transactionAmount', Number(e.target.value))}
              min="0"
              step="0.01"
              placeholder="0.00"
            />
          </div>
        </div>
      )}

      {targetingType === 'customer_points' && (
        <div className="targeting-criteria">
          <div className="form-group">
            <label>Member Tiers</label>
            <select
              multiple
              value={targetingCriteria.memberTiers}
              onChange={(e) => handleCriteriaChange('memberTiers', selectedOptions(e))}
              size="5"
            >
              {MEMBER_TIER_OPTIONS.map(tier => <option key={tier} value={tier}>{tier}</option>)}
            </select>
            <div className="form-helper-text">Hold Ctrl/Cmd to select multiple tiers</div>
          </div>

          <div className="form-group">
            <label>Points Criteria</label>
            <select
              value={targetingCriteria.pointsType}
              onChange={(e) => handleCriteriaChange('pointsType', e.target.value)}
            >
              <option value="not_selected">Not Selected</option>
              <option value="greater_than">Greater Than the Following Number of Points</option>
              <option value="less_than">Less Than the Following Number of Points</option>
            </select>
          </div>

          <div className="form-group">
            <label>Number of Points</label>
            <input
              type="number"
              value={targetingCriteria.pointsAmount}
              onChange={(e) => handleCriteriaChange('pointsAmount', Number(e.target.value))}
              min="0"
              placeholder="0"
            />
          </div>
        </div>
      )}

      {targetingType === 'customer_appointment' && (
        <div className="targeting-criteria">
          <div className="form-group">
            <label>Upcoming Request within</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="number"
                value={targetingCriteria.appointmentTimeValue}
                onChange={(e) => handleCriteriaChange('appointmentTimeValue', Number(e.target.value))}
                min="0"
                style={{ width: '80px' }}
              />
              <select
                value={targetingCriteria.appointmentTimeUnit}
                onChange={(e) => handleCriteriaChange('appointmentTimeUnit', e.target.value)}
              >
                <option value="hours">Hours</option>
                <option value="days">Days</option>
                <option value="months">Months</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Service Requested</label>
            <select
              value={targetingCriteria.appointmentService}
              onChange={(e) => handleCriteriaChange('appointmentService', e.target.value)}
              disabled={loadingServicesStaff}
            >
              <option value="not_selected">Not Selected (Any Service)</option>
              {salonServices.map(service => (
                <option key={service.id} value={service.id}>{service.name}</option>
              ))}
            </select>
            {loadingServicesStaff && <span className="form-helper-text">Loading services...</span>}
          </div>

          <div className="form-group">
            <label>Staff Requested</label>
            <select
              value={targetingCriteria.appointmentStaff}
              onChange={(e) => handleCriteriaChange('appointmentStaff', e.target.value)}
              disabled={loadingServicesStaff}
            >
              <option value="not_selected">Not Selected (Any Staff)</option>
              {salonStaff.map(staff => (
                <option key={staff.id} value={staff.id}>{staff.name}</option>
              ))}
            </select>
            {loadingServicesStaff && <span className="form-helper-text">Loading staff...</span>}
          </div>

          <div className="form-group">
            <label>Appointment Status</label>
            <select
              multiple
              value={targetingCriteria.appointmentStatus}
              onChange={(e) => handleCriteriaChange('appointmentStatus', selectedOptions(e))}
              size="4"
            >
              {APPOINTMENT_STATUS_OPTIONS.map(status => (
                <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
              ))}
            </select>
            <div className="form-helper-text">
              Hold Ctrl/Cmd to select multiple statuses. Leave empty for any status.
            </div>
          </div>
        </div>
      )}

      {targetingType === 'specific_customer' && (
        <div className="targeting-criteria">
          <div className="form-group">
            <label>
              Search and Select Customers
              {errors.specificCustomers && <span className="error-text"> - {errors.specificCustomers}</span>}
            </label>
            <div className="customer-search-container">
              <input
                type="text"
                value={customerSearchQuery}
                onChange={handleCustomerSearchChange}
                onFocus={() => setShowCustomerDropdown(true)}
                placeholder="Search by customer name or email..."
                className="customer-search-input"
              />

              {showCustomerDropdown && (
                <div className="customer-search-dropdown">
                  {searchingCustomers && <div className="customer-search-loading">Searching...</div>}

                  {!searchingCustomers && customerSearchResults.length === 0 && customerSearchQuery.length >= 2 && (
                    <div className="customer-search-no-results">No customers found</div>
                  )}

                  {!searchingCustomers && customerSearchResults.map(customer => (
                    <div
                      key={customer.id}
                      className="customer-search-result"
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      <div className="customer-result-name">{customer.displayName}</div>
                      <div className="customer-result-email">{customer.email}</div>
                      {customer.phoneNumber && (
                        <div className="customer-result-phone">{customer.phoneNumber}</div>
                      )}
                    </div>
                  ))}

                  {customerSearchQuery.length < 2 && (
                    <div className="customer-search-hint">Type at least 2 characters to search</div>
                  )}
                </div>
              )}
            </div>

            {targetingCriteria.specificCustomers.length > 0 && (
              <div className="selected-customers">
                <h5>Selected Customers ({targetingCriteria.specificCustomers.length})</h5>
                <div className="selected-customers-list">
                  {targetingCriteria.specificCustomers.map(customer => (
                    <div key={customer.id} className="selected-customer-item">
                      <div className="selected-customer-info">
                        <div className="selected-customer-name">{customer.displayName}</div>
                        <div className="selected-customer-email">{customer.email}</div>
                      </div>
                      <button
                        type="button"
                        className="remove-customer-button"
                        onClick={() => handleCustomerRemove(customer.id)}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {targetingType === 'customer_notes' && (
        <div className="targeting-criteria">
          <div className="form-group">
            <label>Search Terms</label>
            <input
              type="text"
              value={targetingCriteria.notesSearchText || ''}
              onChange={(e) => handleCriteriaChange('notesSearchText', e.target.value)}
              placeholder="Enter words or phrase to search in customer notes..."
            />
            <div className="form-helper-text">Customers whose notes match the search terms will be targeted</div>
          </div>
          <div className="form-group">
            <label>Match Type</label>
            <select
              value={targetingCriteria.notesMatchType || 'contains_all'}
              onChange={(e) => handleCriteriaChange('notesMatchType', e.target.value)}
            >
              <option value="exact">Exact Match — notes contain this exact phrase</option>
              <option value="contains_any">Contains Any Words — notes contain at least one of the words</option>
              <option value="contains_all">Contains All Words — notes contain all words (any order)</option>
            </select>
          </div>
        </div>
      )}

      <div className="targeting-buttons">
        <button
          type="button"
          className="save-criteria-button"
          onClick={handleSaveCriteria}
          disabled={targetingType === 'not_selected'}
        >
          Save Targeting Criteria
        </button>
        <button
          type="button"
          className="preview-button"
          onClick={handlePreview}
          disabled={previewLoading || !criteriaSaved}
          style={{ opacity: criteriaSaved ? 1 : 0.5 }}
        >
          {previewLoading ? 'Loading...' : 'Preview Target Customers'}
        </button>
        <button
          type="button"
          className="download-social-csv-button"
          onClick={handleDownloadSocialCSV}
          disabled={!criteriaSaved}
          style={{ opacity: criteriaSaved ? 1 : 0.5 }}
        >
          Download Target for Social Platforms (Meta, Instagram, TikTok)
        </button>
      </div>
    </div>
  );
}

export default PersonalisationTargeting;
